/**
 * ai-connector-tab.tsx — "Kết nối AI (Claude)" (Cài đặt hệ thống → Tổ chức). MCP connector, docs/THIET-KE-MCP-CONNECTOR.md.
 *
 * Mọi người dùng đều vào được: xem URL kết nối + hướng dẫn, xem / thu hồi kết nối CỦA MÌNH. Phần quản trị theo cờ backend trả:
 *  - `canManage` (ai_connector.update): công tắc bật/tắt cả tổ chức (bật = đồng ý điều khoản, ghi consent; tắt = thu hồi mọi kết nối),
 *    cho Claude xem SĐT/email đầy đủ (ecdp.pii), cho Claude tạo bản nháp (ecdp.write).
 *  - `canViewAll` (ai_connector.view): bảng kết nối của cả tổ chức; thu hồi của người khác backend tự kiểm ai_connector.delete.
 */
import { useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Bot, Copy, Loader2, Plug, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox, Switch } from '@/components/ui/misc'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Loading, EmptyState, ErrorState } from '@/components/shared/feedback'
import {
  useMcpConnections, useMcpSettings, useRevokeMcpConnection, useUpdateMcpConnectionWriteScope, useUpdateMcpSettings, type McpConnection,
} from '@/hooks/use-mcp-connector'
import { apiError } from '@/lib/api-client'
import { mcpScopeShortLabel } from '@/lib/mcp-connector'
import { currentLocale, i18n } from '@/lib/i18n'
import { McpPendingWritesCard } from './mcp-pending-writes-card'

const copy = (text: string, label: string) => {
  navigator.clipboard.writeText(text).then(
    () => toast.success(i18n.t('settings:aiConnector.copied', { label })),
    () => toast.error(i18n.t('settings:aiConnector.copyBlocked')),
  )
}

export function AiConnectorTab() {
  const { t } = useTranslation('settings')
  const { data: s, isLoading, error, refetch } = useMcpSettings()
  const update = useUpdateMcpSettings()
  // Hộp xác nhận: bật (kèm điều khoản) hoặc tắt (thu hồi mọi kết nối).
  const [confirm, setConfirm] = useState<'enable' | 'disable' | 'autoHighRisk' | null>(null)
  const [agreed, setAgreed] = useState(false)

  if (isLoading) return <Loading label={t('settings:aiConnector.loading')} />
  if (error || !s) return <ErrorState message={apiError(error)} onRetry={() => refetch()} />

  const save = async (body: Parameters<typeof update.mutateAsync>[0], ok: string) => {
    try {
      const res = await update.mutateAsync(body)
      toast.success(body.enabled === false && res.revoked ? t('settings:aiConnector.savedWithRevoked', { message: ok, count: res.revoked }) : ok)
      setConfirm(null)
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const claudeCodeCmd = `claude mcp add --transport http ecdp ${s.mcpUrl}`

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">
            <Bot className="h-4 w-4 text-primary" /> {t('settings:aiConnector.title')}
            {s.enabled ? <Badge variant="success">{t('settings:aiConnector.statusOn')}</Badge> : <Badge variant="secondary">{t('settings:aiConnector.statusOff')}</Badge>}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {t('settings:aiConnector.description')}
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {s.canManage && (
            <div className="space-y-3 rounded-lg border p-3">
              <SwitchRow
                id="mcp-enabled"
                label={t('settings:aiConnector.enableLabel')}
                hint={t('settings:aiConnector.enableHint')}
                checked={s.enabled}
                disabled={update.isPending}
                onChange={(v) => { setAgreed(false); setConfirm(v ? 'enable' : 'disable') }}
              />
              {s.enabled && (
                <>
                  <SwitchRow
                    id="mcp-pii"
                    label={t('settings:aiConnector.piiLabel')}
                    hint={t('settings:aiConnector.piiHint')}
                    checked={s.allowPii}
                    disabled={update.isPending}
                    onChange={(v) => save({ allowPii: v }, v ? t('settings:aiConnector.piiOn') : t('settings:aiConnector.piiOff'))}
                  />
                  <SwitchRow
                    id="mcp-write"
                    label={t('settings:aiConnector.writeLabel')}
                    hint={t('settings:aiConnector.writeHint')}
                    checked={s.allowWrite}
                    disabled={update.isPending}
                    onChange={(v) => save({ allowWrite: v }, v ? t('settings:aiConnector.writeOn') : t('settings:aiConnector.writeOff'))}
                  />
                  {s.allowWrite && (
                    <>
                      <SwitchRow
                        id="mcp-auto"
                        label={t('settings:aiConnector.autoLabel')}
                        hint={t('settings:aiConnector.autoHint')}
                        checked={s.autoMode}
                        disabled={update.isPending}
                        onChange={(v) => save({ autoMode: v }, v ? t('settings:aiConnector.autoOn') : t('settings:aiConnector.autoOff'))}
                      />
                      <SwitchRow
                        id="mcp-auto-high"
                        label={t('settings:aiConnector.autoHighLabel')}
                        hint={t('settings:aiConnector.autoHighHint')}
                        checked={s.autoModeHighRisk}
                        disabled={update.isPending || !s.autoMode}
                        warning
                        onChange={(v) => (v
                          ? setConfirm('autoHighRisk')
                          : save({ autoModeHighRisk: false }, t('settings:aiConnector.autoHighOff')))}
                      />
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {!s.enabled && !s.canManage && (
            <p className="rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
              {t('settings:aiConnector.notEnabled')}
            </p>
          )}

          <div className="space-y-1.5">
            <p className="text-sm font-medium">{t('settings:aiConnector.urlLabel')}</p>
            <div className="flex items-center gap-2">
              <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1.5 text-xs">{s.mcpUrl}</code>
              <Button size="sm" variant="outline" onClick={() => copy(s.mcpUrl, 'URL')}>
                <Copy className="h-3.5 w-3.5" /> {t('settings:aiConnector.copy')}
              </Button>
            </div>
          </div>

          <div className="space-y-2 text-xs text-muted-foreground">
            <p className="text-sm font-medium text-foreground">{t('settings:aiConnector.howToTitle')}</p>
            <ol className="list-decimal space-y-1 pl-4">
              <li>
                <span className="text-foreground">{t('settings:aiConnector.howToWebLabel')}</span> {t('settings:aiConnector.howToWebSteps')}
              </li>
              <li>
                <span className="text-foreground">Claude Code:</span> {t('settings:aiConnector.howToCliSteps')}
                <div className="mt-1 flex items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded bg-muted px-2 py-1">{claudeCodeCmd}</code>
                  <Button size="sm" variant="ghost" onClick={() => copy(claudeCodeCmd, t('settings:aiConnector.copyLabelCommand'))} aria-label={t('settings:aiConnector.copyCommandAria')}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            </ol>
          </div>
        </CardContent>
      </Card>

      {s.canApprove && <McpPendingWritesCard pendingCount={s.pendingWrites} />}
      <ConnectionsCard all={false} orgAllowWrite={s.allowWrite} />
      {s.canViewAll && <ConnectionsCard all orgAllowWrite={s.allowWrite} />}

      <Dialog open={confirm !== null} onOpenChange={(o) => !o && setConfirm(null)}>
        <DialogContent className="max-w-lg">
          {confirm === 'enable' ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('settings:aiConnector.enableDialog.title')}</DialogTitle>
                <DialogDescription>{t('settings:aiConnector.enableDialog.description')}</DialogDescription>
              </DialogHeader>
              <ul className="list-disc space-y-1.5 pl-5 text-sm text-muted-foreground">
                <li>{t('settings:aiConnector.enableDialog.termDataTransfer')}</li>
                <li>{t('settings:aiConnector.enableDialog.termController')}</li>
                <li>{t('settings:aiConnector.enableDialog.termPlan')}</li>
                <li>{t('settings:aiConnector.enableDialog.termPii')}</li>
                <li>{t('settings:aiConnector.enableDialog.termAudit')}</li>
              </ul>
              <div className="flex items-start gap-2 text-sm">
                <Checkbox id="mcp-terms" className="mt-0.5" checked={agreed} onCheckedChange={(v) => setAgreed(v === true)} />
                <label htmlFor="mcp-terms">{t('settings:aiConnector.enableDialog.agree', { version: s.termsVersion })}</label>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirm(null)}>{t('settings:aiConnector.cancel')}</Button>
                <Button
                  disabled={!agreed || update.isPending}
                  onClick={() => save({ enabled: true, acceptTerms: true }, t('settings:aiConnector.enabled'))}
                >
                  {update.isPending && <Loader2 className="animate-spin" />}
                  {t('common:actions.enable')}
                </Button>
              </DialogFooter>
            </>
          ) : confirm === 'autoHighRisk' ? (
            <>
              <DialogHeader>
                <DialogTitle>{t('settings:aiConnector.autoHighDialog.title')}</DialogTitle>
                <DialogDescription>{t('settings:aiConnector.autoHighDialog.description')}</DialogDescription>
              </DialogHeader>
              <ul className="list-disc space-y-1.5 pl-5 text-sm text-destructive">
                <li>{t('settings:aiConnector.autoHighDialog.riskDelete')}</li>
                <li>{t('settings:aiConnector.autoHighDialog.riskSend')}</li>
                <li>{t('settings:aiConnector.autoHighDialog.riskCampaign')}</li>
              </ul>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirm(null)}>{t('settings:aiConnector.cancel')}</Button>
                <Button
                  variant="destructive"
                  disabled={update.isPending}
                  onClick={() => save({ autoModeHighRisk: true }, t('settings:aiConnector.autoHighOn'))}
                >
                  {update.isPending && <Loader2 className="animate-spin" />}
                  {t('settings:aiConnector.autoHighDialog.confirm')}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>{t('settings:aiConnector.disableDialog.title')}</DialogTitle>
                <DialogDescription>
                  {t('settings:aiConnector.disableDialog.description')}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirm(null)}>{t('settings:aiConnector.cancel')}</Button>
                <Button variant="destructive" disabled={update.isPending} onClick={() => save({ enabled: false }, t('settings:aiConnector.disabled'))}>
                  {update.isPending && <Loader2 className="animate-spin" />}
                  {t('settings:aiConnector.disableDialog.confirm')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

function SwitchRow({ id, label, hint, checked, disabled, warning, onChange }: {
  id: string; label: string; hint: string; checked: boolean; disabled?: boolean; warning?: boolean; onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-3">
      <label htmlFor={id} className="min-w-0 text-sm">
        {label}
        <span className={`block text-xs ${warning ? 'text-warning' : 'text-muted-foreground'}`}>{hint}</span>
      </label>
      <Switch id={id} checked={checked} disabled={disabled} onCheckedChange={onChange} />
    </div>
  )
}

/** Bảng kết nối: của tôi (`all` = false) hoặc cả tổ chức (thêm cột người dùng). Danh sách thẻ — vừa điện thoại.
 *  Chỉ kết nối CỦA TÔI (all = false) mới sửa được quyền ghi — backend chỉ cho chủ kết nối tự sửa (không phải quản trị viên khác). */
function ConnectionsCard({ all, orgAllowWrite }: { all: boolean; orgAllowWrite: boolean }) {
  const { t } = useTranslation('settings')
  const { data: rows, isLoading, error, refetch } = useMcpConnections(all)
  const revoke = useRevokeMcpConnection()
  const updateWrite = useUpdateMcpConnectionWriteScope()

  // Ba scope ghi TÁCH BIỆT: ecdp.write (huấn luyện / nháp), ecdp.send (gửi tin thật + chiến dịch), ecdp.delete (xoá dữ liệu).
  const toggleScope = async (c: McpConnection, cap: 'write' | 'send' | 'delete', v: boolean) => {
    const key = { write: 'allowWrite', send: 'allowSend', delete: 'allowDelete' }[cap]
    try {
      await updateWrite.mutateAsync({ id: c.id, [key]: v })
      toast.success(t(`settings:aiConnector.connections.${cap}${v ? 'On' : 'Off'}`))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  const remove = async (c: McpConnection) => {
    const question = all
      ? t('settings:aiConnector.connections.revokeConfirmOther', { client: c.clientName, user: c.user.fullName })
      : t('settings:aiConnector.connections.revokeConfirm', { client: c.clientName })
    if (!window.confirm(question)) return
    try {
      await revoke.mutateAsync(c.id)
      toast.success(t('settings:aiConnector.connections.revoked'))
    } catch (e) {
      toast.error(apiError(e))
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Plug className="h-4 w-4 text-primary" /> {all ? t('settings:aiConnector.connections.orgTitle') : t('settings:aiConnector.connections.mineTitle')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Loading label={t('settings:aiConnector.connections.loading')} />
        ) : error ? (
          <ErrorState message={apiError(error)} onRetry={() => refetch()} />
        ) : !rows?.length ? (
          <EmptyState
            icon={Plug}
            title={t('settings:aiConnector.connections.empty')}
            description={all ? undefined : t('settings:aiConnector.connections.emptyHint')}
          />
        ) : (
          <ul className="divide-y rounded-lg border">
            {rows.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                    <span className="truncate">{c.clientName}</span>
                    {c.scopes.map((sc) => (
                      <Badge key={sc} variant={sc === 'ecdp.read' ? 'secondary' : 'default'}>{mcpScopeShortLabel(sc)}</Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {all && <>{c.user.fullName} ({c.user.email}) · </>}
                    {c.redirectHost} · {t('settings:aiConnector.connections.connectedAt', { date: new Date(c.createdAt).toLocaleDateString(currentLocale()) })}
                    {c.lastUsedAt
                      ? ` · ${t('settings:aiConnector.connections.lastUsedAt', { date: new Date(c.lastUsedAt).toLocaleString(currentLocale()) })}`
                      : ` · ${t('settings:aiConnector.connections.neverUsed')}`}
                  </p>
                </div>
                {!all && (
                  <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                    {(['write', 'send', 'delete'] as const).map((cap) => {
                      const scope = `ecdp.${cap}`
                      return (
                        <label
                          key={cap}
                          className="flex items-center gap-1.5 text-xs text-muted-foreground"
                          title={orgAllowWrite ? t(`settings:aiConnector.connections.${cap}Hint`) : t('settings:aiConnector.connections.writeDisabledByOrg')}
                        >
                          {t(`settings:aiConnector.connections.${cap}Label`)}
                          <Switch
                            checked={c.scopes.includes(scope)}
                            disabled={updateWrite.isPending || (!orgAllowWrite && !c.scopes.includes(scope))}
                            onCheckedChange={(v) => toggleScope(c, cap, v)}
                            aria-label={t(`settings:aiConnector.connections.${cap}Label`)}
                          />
                        </label>
                      )
                    })}
                  </div>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => remove(c)}
                  aria-label={t('settings:aiConnector.connections.revokeAria', { client: c.clientName })}
                >
                  <Trash2 className="h-4 w-4 text-destructive" />
                  <span className="hidden sm:inline">{t('settings:aiConnector.connections.revoke')}</span>
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  )
}
